import { captureMessage } from '@sentry/react';
import { isShareableUrlSplitOn } from './isShareableUrlSplitOn';
import {
	FilteringOptionType,
	FiltersParameterForPostRequest,
	getActiveFiltersFromSessionStorage,
	mapFilteringOptionsToFiltersParam,
	readFiltersParamFromSessionStorage,
	SearchOperator,
} from './statelessFiltersFromSessionStorage';
import { DroppedUrlFilterReason, readFiltersParamFromUrl } from './statelessFiltersFromUrl';

const reportedReasons = new Set<DroppedUrlFilterReason>();

// Once per reason per page load, so a filtered page doesn't report on every debounced keystroke.
const reportDroppedUrlFilter = (reason: DroppedUrlFilterReason) => {
	if (reportedReasons.has(reason)) {
		return;
	}

	reportedReasons.add(reason);
	captureMessage(
		`Unified Header | Spotlight sent no clause for a shareable-URL filter: ${reason}`,
	);
};

/**
 * The applied filters the dropdown searches within: with shareable URLs on, the URL is paramount's
 * source of truth, so read it; otherwise read paramount's copy in `sessionStorage`, as before.
 */
export const readAppliedFilters = (
	searchOperator: SearchOperator,
): FiltersParameterForPostRequest =>
	isShareableUrlSplitOn()
		? readFiltersParamFromUrl(searchOperator, { onDropped: reportDroppedUrlFilter })
		: readFiltersParamFromSessionStorage(searchOperator);

/**
 * The applied filters a hybrid search keeps: paramount replaces any previously-applied search
 * terms and any similarity filter on the asset bank with the new hybrid term, so the ASSET
 * spotlight results leave them out. The URL path never carries similarity.
 */
export const readAppliedFiltersForHybridSearch = (
	searchOperator: SearchOperator,
): FiltersParameterForPostRequest => {
	if (isShareableUrlSplitOn()) {
		const { filters } = readFiltersParamFromUrl(searchOperator, {
			onDropped: reportDroppedUrlFilter,
		});

		return { filters };
	}

	const keptOptions = getActiveFiltersFromSessionStorage().filter(
		(option) =>
			option.type !== FilteringOptionType.searchTerm &&
			option.type !== FilteringOptionType.similarityFilter,
	);

	return mapFilteringOptionsToFiltersParam(keptOptions, searchOperator);
};
